import { asRecord, parseJson } from "@arti/utilities";

type ParsedWebSocketObject = Record<string, unknown>;

function nowMs() {
  return Date.now();
}

function clampInt(
  value: number,
  { min, max }: { min: number; max: number },
): number {
  if (!Number.isFinite(value)) {
    return min;
  }
  return Math.max(min, Math.min(max, Math.floor(value)));
}

function jitter(ms: number, spread = 0.2) {
  const delta = ms * spread;
  return clampInt(ms - delta + Math.random() * delta * 2, {
    max: Number.MAX_SAFE_INTEGER,
    min: 0,
  });
}

function extractRawText(message: MessageEvent["data"]): string | null {
  if (typeof message === "string") {
    return message;
  }
  if (message instanceof ArrayBuffer) {
    return new TextDecoder().decode(message);
  }
  return null;
}

function safeParseJson(
  message: MessageEvent["data"],
): ParsedWebSocketObject | null {
  const raw = extractRawText(message);
  if (!raw) {
    return null;
  }
  return asRecord(parseJson(raw));
}

export class RealtimeWebSocketController {
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private attempt = 0;

  constructor(
    private readonly options: {
      getUrl: () => string;
      shouldReconnect: () => boolean;
      onConnecting: (attempt: number) => void;
      onOpen: () => void;
      onDisconnect: (event: {
        kind: "close" | "error";
        shouldReconnect: boolean;
      }) => void;
      onMessage: (message: ParsedWebSocketObject) => void;
    },
  ) {}

  isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  sendText(text: string): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) {
      return false;
    }
    try {
      this.ws.send(text);
      return true;
    } catch {
      return false;
    }
  }

  ensureConnected(): void {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.attempt += 1;
    this.options.onConnecting(this.attempt);

    const ws = new WebSocket(this.options.getUrl());
    this.ws = ws;

    ws.addEventListener("open", () => {
      this.attempt = 0;
      this.options.onOpen();
    });

    ws.addEventListener("close", () => {
      const shouldReconnect = this.options.shouldReconnect();
      this.options.onDisconnect({ kind: "close", shouldReconnect });
      if (shouldReconnect) {
        this.scheduleReconnect();
      }
    });

    ws.addEventListener("error", () => {
      const shouldReconnect = this.options.shouldReconnect();
      this.options.onDisconnect({ kind: "error", shouldReconnect });
      if (shouldReconnect) {
        this.scheduleReconnect();
      }
    });

    ws.addEventListener("message", (event) => {
      const parsed = safeParseJson(event.data);
      if (!parsed) {
        return;
      }
      this.options.onMessage(parsed);
    });
  }

  close(reason: string): void {
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, reason);
      } catch {
        // Ignore
      }
    }
    this.ws = null;
    this.attempt = 0;
  }

  scheduleReconnect(overrideDelayMs?: number): void {
    if (this.reconnectTimer !== null) {
      return;
    }
    if (!this.options.shouldReconnect()) {
      return;
    }

    const base = 500;
    const max = 10_000;
    const attempt = Math.max(1, this.attempt);
    const delay =
      overrideDelayMs ??
      jitter(Math.min(max, Math.floor(base * 1.7 ** attempt)));

    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.options.shouldReconnect()) {
        return;
      }
      this.ensureConnected();
    }, delay);
  }

  updateUrlAndReconnectSoon({
    shouldReconnect,
    overrideDelayMs,
  }: {
    shouldReconnect: boolean;
    overrideDelayMs?: number;
  }): void {
    if (!shouldReconnect) {
      return;
    }
    this.scheduleReconnect(overrideDelayMs ?? 0);
  }

  markMessageReceived(): number {
    return nowMs();
  }
}
